import { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { organizationService } from '../../services/organizationService';
import type { Organization } from '../../types/organization';

interface OrganizationAutocompleteProps {
  companyId: number;
  value: Organization | null;
  onChange: (value: Organization | null) => void;
  disabled?: boolean;
}

// Organizations are optional on Contact and expected to be few per company, so the full
// active list is fetched once and filtered locally — same pattern as
// NovaPoshtaWarehouseSelect — rather than wiring incremental search into the picker.
export default function OrganizationAutocomplete({
  companyId,
  value,
  onChange,
  disabled,
}: OrganizationAutocompleteProps) {
  const [options, setOptions] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    organizationService.listAllActive(companyId)
      .then(setOptions)
      .catch(() => setError('Не вдалося завантажити список організацій'))
      .finally(() => setLoading(false));
  }, [companyId]);

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Організація"
          fullWidth
          margin="normal"
          error={!!error}
          helperText={error ?? 'Необов’язково'}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
